import { useQueries, useQueryClient } from 'react-query';

import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { GET_LEADS_PATH } from 'src/services/api/leads/constants';
import { getLeadFactsQueryKey, type LeadFactKey } from 'src/services/api/leads/lead-facts';

/**
 * Persist client-detected regex values (phone/email/pet) as `pending` suggestions so
 * they share the LLM suggestion lifecycle and their accept/reject is tracked. Fires
 * one idempotent POST per `(lead, key, value)` — the server upserts by (lead, key),
 * and once the pending row comes back via the invalidated suggestions query the value
 * is no longer re-detected, so it isn't re-posted. Renders nothing.
 */
export const usePersistRegexSuggestions = (
    leadUuid: string,
    detections: { key: LeadFactKey; value: string }[],
    enabled: boolean
) => {
    const queryClient = useQueryClient();
    useQueries(
        detections.map((detection) => ({
            queryKey: [queryKeys.factSuggestions, 'regex', leadUuid, detection.key, detection.value],
            queryFn: async () => {
                const res = await apiClient(`${GET_LEADS_PATH}/${leadUuid}/suggestions`, {
                    method: 'post',
                    json: { key: detection.key, suggested_value: detection.value },
                });
                // 201 = a pending row was created/refreshed; 200 = a guardrail declined
                // it (no-op, nothing changed). Mirrors the extraction run's 'ran' outcome.
                return res.status === 201;
            },
            enabled,
            retry: false,
            staleTime: Infinity,
            onSuccess: (created: boolean) => {
                if (created) {
                    queryClient.invalidateQueries(getLeadFactsQueryKey(leadUuid));
                }
            },
        }))
    );
};
